import { useTheme } from "@/context/ThemeContext";

const problems = [
  "Missed calls.",
  "Slow responses.",
  "Manual follow-ups.",
  "Leads that never convert.",
];

export default function CoreValueSection() {
  const { theme } = useTheme();
  const isDark = theme === "dark";

  return (
    <section className="relative py-32 md:py-44 overflow-hidden">
      {/* Full-bleed background image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/50b37c2a7bab91c04032c9ef8d8d38f7.jpg"
          alt=""
          className="w-full h-full object-cover object-center"
        />
        {/* Adaptive overlay — light mode gets a near-white wash, dark mode stays deep */}
        <div
          className={`absolute inset-0 transition-colors duration-300 ${
            isDark
              ? "bg-gradient-to-br from-[#0a0a0a]/90 via-[#0a0a0a]/82 to-[#0a0a0a]/90"
              : "bg-gradient-to-br from-white/93 via-white/87 to-white/93"
          }`}
        />
        {/* Subtle emerald accent glow */}
        <div
          className={`absolute bottom-0 right-0 w-[600px] h-[600px] rounded-full blur-[160px] pointer-events-none ${
            isDark ? "bg-emerald-600/8" : "bg-emerald-400/10"
          }`}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left: Visual */}
          <div className="relative">
            <div className="rounded-3xl overflow-hidden aspect-[4/5]">
              <img
                src="https://readdy.ai/api/search-image?query=modern%20business%20professional%20looking%20at%20data%20analytics%20dashboard%20on%20large%20screen%20dark%20office%20environment%20dramatic%20lighting%20cinematic%20composition%20high%20contrast%20technology%20workspace%20premium&width=800&height=1000&seq=core-value-img-001&orientation=portrait"
                alt="Business system problem"
                className="w-full h-full object-cover object-top"
              />
              <div
                className={`absolute inset-0 rounded-3xl ${
                  isDark
                    ? "bg-gradient-to-t from-[#0a0a0a]/60 to-transparent"
                    : "bg-gradient-to-t from-black/30 to-transparent"
                }`}
              />
            </div>
            {/* Floating stat card */}
            <div
              className={`absolute bottom-8 left-8 right-8 rounded-2xl p-5 backdrop-blur-md border ${
                isDark ? "bg-white/8 border-white/15" : "bg-white/90 border-black/8"
              }`}
            >
              <p
                className={`text-xs tracking-widest uppercase font-semibold mb-1 ${
                  isDark ? "text-white/40" : "text-[#6e6e73]"
                }`}
              >
                Revenue Lost to System Gaps
              </p>
              <p className={`text-2xl font-black ${isDark ? "text-white" : "text-[#1d1d1f]"}`}>
                Up to 40% of leads
              </p>
              <p className={`text-sm mt-1 ${isDark ? "text-white/50" : "text-[#6e6e73]"}`}>
                never get a timely follow-up
              </p>
            </div>
          </div>

          {/* Right: Text */}
          <div>
            <p
              className={`text-[11px] tracking-[0.15em] uppercase font-semibold mb-6 ${
                isDark ? "text-white/35" : "text-[#6e6e73]"
              }`}
            >
              The Real Problem
            </p>
            <h2
              className={`text-4xl md:text-5xl font-black leading-[1.08] tracking-[-0.02em] mb-10 ${
                isDark ? "text-white" : "text-[#1d1d1f]"
              }`}
            >
              Most service businesses don&apos;t have a lead problem.
              <br />
              <span className={isDark ? "text-white/35" : "text-[#6e6e73]"}>
                They have a system problem.
              </span>
            </h2>

            <div className="flex flex-col gap-3 mb-10">
              {problems.map((problem) => (
                <div
                  key={problem}
                  className={`flex items-center gap-4 py-3 border-b ${
                    isDark ? "border-white/8" : "border-black/6"
                  }`}
                >
                  <div className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" />
                  <p
                    className={`text-xl font-light ${
                      isDark ? "text-white/65" : "text-[#1d1d1f]/65"
                    }`}
                  >
                    {problem}
                  </p>
                </div>
              ))}
            </div>

            <div
              className={`rounded-2xl p-6 ${
                isDark
                  ? "bg-white/5 border border-white/8"
                  : "bg-white/70 border border-black/5 backdrop-blur-sm"
              }`}
            >
              <p
                className={`text-lg font-semibold mb-2 ${
                  isDark ? "text-white" : "text-[#1d1d1f]"
                }`}
              >
                We fix that — completely.
              </p>
              <p
                className={`text-sm leading-relaxed ${
                  isDark ? "text-white/50" : "text-[#6e6e73]"
                }`}
              >
                Complete AI Business Solutions installs fully integrated systems that handle your
                operations automatically, from first contact to final conversion.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
